import './submit.css';
import type { Fx, PhotoSubmitOutcome, PhotoSubmitRequest } from '../../tabs/contract';
import type { SubmitResult } from '../../api/types';
import { RpcError } from '../../api/errors';
import { outbox, type PendingSubmit, type SendOutcome } from '../../core/outbox';
import { session } from '../../core/session';
import { geo } from '../../core/geo';
import { store } from '../../core/store';
import { serverNow } from '../../core/clock';
import { preparePhoto } from '../../core/image';
import { claimItem, flowEnded, flowStarted, releaseItem } from './owned';
import { diceRequestFrom } from './dice';
import { codeOf, failScreen, hornetInfo, type FailAction, type FailScreen } from './errors';

/** 8秒たっても返事がないとき、電波の注意を出す（㉛b） */
const SLOW_MS = 8000;
/** 撮影を取りやめたとみなす待ち（選択画面から戻ったのに change が来ない端末用） */

function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

type Stage = 'capture' | 'confirm' | 'sending' | 'fail' | 'flushing' | 'result' | 'done';

class Flow {
  private root = h('div', 'sb-flow');
  private page = h('div', 'sb-page');
  private input = document.createElement('input');
  private stage: Stage = 'capture';
  private resolveFn!: (o: PhotoSubmitOutcome) => void;
  private finished = false;

  private photo: { blob: Blob; url: string; w: number; h: number } | null = null;
  private takenAt = new Date().toISOString();
  private reading = false;
  private gotFile = false;
  private item: PendingSubmit | null = null;
  private lastError: RpcError | null = null;
  private slowTimer: ReturnType<typeof setTimeout> | null = null;
  private unsub: (() => void) | null = null;
  private bar: HTMLElement | null = null;
  private goTx: HTMLElement | null = null;

  constructor(
    private fx: Fx,
    private host: HTMLElement,
    private req: PhotoSubmitRequest,
  ) {}

  run(): Promise<PhotoSubmitOutcome> {
    flowStarted();
    return new Promise((resolve) => {
      this.resolveFn = resolve;
      if (getComputedStyle(this.host).position === 'static') this.host.style.position = 'relative';
      this.root.dataset.layout = this.host.dataset.sbLayout === 'book' ? 'book' : 'plain';
      this.root.dataset.stage = 'capture';
      this.root.append(this.page);
      this.page.hidden = true;
      // 撮影の選択を出しているあいだ、紙の上に透明な層を置いて下の操作を止める。選択を閉じたあとに押せば取りやめ
      this.root.addEventListener('click', (e) => {
        // input.click() のクリックもここまで上がってくるので除く（除かないと開いた瞬間に取りやめになる）
        if (e.target === this.input) return;
        if (this.stage === 'capture') this.onCancel();
      });
      this.input.type = 'file';
      this.input.accept = 'image/*';
      this.input.setAttribute('capture', 'environment');
      this.input.hidden = true;
      this.input.addEventListener('change', () => void this.onFile());
      this.input.addEventListener('cancel', () => this.onCancel());
      this.root.append(this.input);
      this.host.append(this.root);
      this.openCamera(); // ユーザー操作の中で同期的に呼ぶ（iOS はここを非同期にするとカメラが開かない）
    });
  }

  // ------------------------------------------------------------ 撮影

  private openCamera(): void {
    this.stage = this.photo ? 'confirm' : 'capture';
    this.gotFile = false;
    this.input.value = '';
    this.input.click();
  }

  /** 撮らずに閉じた。写真がまだ無ければ流れごと取りやめ、確認の頁にいたら今の写真に戻る */
  private onCancel(): void {
    if (this.finished || this.reading) return;
    if (this.photo) {
      this.stage = 'confirm';
      return;
    }
    this.finish({ status: 'cancelled' });
  }

  private async onFile(): Promise<void> {
    const file = this.input.files?.[0];
    if (!file) return;
    this.gotFile = true;
    this.reading = true;
    this.takenAt = new Date().toISOString(); // 撮った時刻＝選んだ時刻（圏外で遅れて届いても受信時刻と分けて残る）
    try {
      const prepared = await preparePhoto(file);
      if (this.finished) return;
      if (this.photo) URL.revokeObjectURL(this.photo.url);
      this.photo = { blob: prepared.blob, url: URL.createObjectURL(prepared.blob), w: prepared.width, h: prepared.height };
      this.reading = false;
      this.showConfirm();
    } catch {
      this.reading = false;
      this.showFail('photo_unreadable');
    }
  }

  // ------------------------------------------------------------ 確認（㉛a）と送信中（㉛b）

  private areaName(): string {
    return store.state?.areas.find((a) => a.area_id === this.req.areaId)?.name ?? '';
  }

  private showConfirm(): void {
    if (!this.photo) return;
    this.stage = 'confirm';
    this.root.dataset.stage = 'confirm';
    this.clearOverlay();
    delete this.root.dataset.sd;
    delete this.root.dataset.slow;

    const top = h('div', 'sb-top');
    top.append(h('small', undefined, this.req.kind === 'spot' ? '提出するスポット' : `写真のお題・${this.areaName()}`), h('b', undefined, this.req.title));

    const ph = h('figure', 'sb-ph');
    const pic = h('div', 'sb-pic');
    pic.style.setProperty('--ar', `${this.photo.w}/${this.photo.h}`);
    const img = h('img');
    img.src = this.photo.url;
    img.alt = '撮った写真';
    pic.append(img);
    for (const c of ['sb-c1', 'sb-c2', 'sb-c3', 'sb-c4']) pic.append(h('i', `sb-cn ${c}`));
    ph.append(pic);

    const note = h('p', 'sb-note');
    note.append(h('span', 'n0', '参加者以外の写りこみに注意してください。'));
    const n1 = h('span', 'n1');
    n1.append(document.createTextNode('電波の弱い場所では時間がかかります。'), document.createElement('br'), document.createTextNode('このままお待ちください。'));
    note.append(n1);

    const btns = h('div', 'sb-btn');
    const re = h('button', 'sb-re', '撮り直す');
    re.type = 'button';
    re.addEventListener('click', () => {
      if (this.stage !== 'confirm') return;
      ph.style.opacity = '0';
      setTimeout(() => (ph.style.opacity = '1'), 250);
      this.openCamera();
    });
    const go = h('button', 'sb-go');
    go.type = 'button';
    go.append(h('span', 'g0', 'この写真で提出する'));
    this.goTx = h('span', 'g1', '写真を送っています');
    this.goTx.setAttribute('role', 'status');
    this.bar = h('i', 'bar');
    go.append(this.goTx, this.bar);
    go.addEventListener('click', () => void this.submit());
    btns.append(re, go);

    this.page.replaceChildren(top, ph, note, btns);
    this.page.hidden = false;
  }

  private setSendingUi(): void {
    this.root.dataset.sd = 'on';
    this.root.dataset.up = '1';
    if (this.goTx) this.goTx.textContent = '写真を送っています';
    if (this.bar) this.bar.style.width = '0';
    delete this.root.dataset.slow;
    if (this.slowTimer) clearTimeout(this.slowTimer);
    this.slowTimer = setTimeout(() => (this.root.dataset.slow = 'on'), SLOW_MS);
  }

  private onProgress = (ratio: number): void => {
    if (this.stage !== 'sending') return;
    if (this.bar && this.root.dataset.up) this.bar.style.width = `${Math.round(Math.min(1, Math.max(0, ratio)) * 100)}%`;
    if (ratio >= 1) {
      // 写真が届いた。残りはサーバーの返事待ち（時間は測れないので、線の上を短い区間が流れる）
      delete this.root.dataset.up;
      if (this.goTx) this.goTx.textContent = this.req.kind === 'spot' ? 'ダイスを振っています' : '提出しています';
    }
  };

  private async submit(): Promise<void> {
    if (this.stage !== 'confirm' || !this.photo) return;
    const s = session.get();
    if (!s) {
      this.lastError = new RpcError('invalid_session');
      this.showFail('invalid_session');
      return;
    }
    this.stage = 'sending';
    this.setSendingUi();
    void this.fx.preload().catch(() => undefined); // 3D の読み込みを送信中に済ませる
    const f = geo.fresh();
    this.item = outbox.newItem({
      kind: this.req.kind,
      target_id: this.req.targetId,
      blob: this.photo.blob,
      client_action_at: this.takenAt,
      lat: f?.lat ?? null,
      lon: f?.lon ?? null,
      acc: f?.acc ?? null,
      event_id: s.event_id,
      team_id: s.team_id,
    });
    claimItem(this.item.id);
    this.unsub ??= outbox.settled.on((out) => {
      if (out.item.id === this.item?.id && (this.stage === 'fail' || this.stage === 'flushing')) void this.onOutcome(out);
    });
    await this.send();
  }

  private async send(): Promise<void> {
    if (!this.item) return;
    this.stage = 'sending';
    const out = await outbox.send(this.item, this.onProgress);
    await this.onOutcome(out);
  }

  // ------------------------------------------------------------ 結果

  private async onOutcome(out: SendOutcome): Promise<void> {
    if (this.finished) return;
    if (this.slowTimer) clearTimeout(this.slowTimer);
    if (out.status === 'sent') return this.onSent(out.result);
    if (out.status === 'queued') return this.showFail('queued');
    this.lastError = out.error;
    return this.showFail(codeOf(out.error));
  }

  private async onSent(result: SubmitResult): Promise<void> {
    this.stage = 'result';
    this.req.closeView?.('submitted');
    const spot = store.state?.spots.find((s) => s.spot_id === this.req.targetId);
    if (this.req.kind === 'spot' && result.dice_value != null) {
      const req = diceRequestFrom(result, spot?.base_point ?? null, this.req.title);
      if (!req) {
        this.finish({ status: 'sent', result });
        return;
      }
      // 机を暗くするのは fx。確認の頁は隠し、結果の表が「図鑑に戻る」で閉じたら流れを終える。
      // 図鑑は本の頁がそのまま床になる。地図から呼んだとき（plain）は紙を敷いたまま残す（隠すと地図の上でダイスが転がる）
      this.clearOverlay();
      if (this.root.dataset.layout === 'plain') {
        this.page.hidden = true;
        this.root.dataset.stage = 'dice';
      } else this.root.style.display = 'none';
      try {
        await this.fx.rollDice(this.host, req);
      } catch (err) {
        console.error(err);
      }
      this.finish({ status: 'sent', result });
      return;
    }
    if (this.req.kind === 'spot') {
      this.showReclear(result);
      return;
    }
    this.finish({ status: 'sent', result });
  }

  /** 縄張り争い・女王の飛行の再クリア。ダイスと得点は無い（README ㉛c「縄張り争いの再提出」。女王は試作が無く最小の形） */
  private showReclear(result: SubmitResult): void {
    this.clearOverlay();
    this.root.dataset.stage = 'result';
    delete this.root.dataset.sd;
    const body = h('div', 'sb-rs-body');
    const top = h('div', 'sb-top');
    top.append(h('small', undefined, 'クリア'), h('b', undefined, this.req.title));
    const tag = h('p', 'sb-tag');
    if (result.duel_reclear_id != null && result.duel) {
      const st = store.state;
      const eff = st?.active_effects.find((x) => x.effect_id === result.duel_effect_id);
      const iAmSource = eff && st ? eff.source_team === st.team.team_id : true;
      const mine = iAmSource ? result.duel.source_count : result.duel.target_count;
      const theirs = iAmSource ? result.duel.target_count : result.duel.source_count;
      tag.append(h('b', undefined, '縄張り争い'), h('span', 'big', `${mine} 対 ${theirs}`), h('small', undefined, 'このクリアは縄張り争いの数にだけ入ります。'));
    } else if (result.queen_reclear_id != null) {
      tag.append(h('b', undefined, '女王の飛行'), h('span', 'big ja', '止めました'));
    } else {
      tag.append(h('b', undefined, '提出'), h('span', 'big ja', '送れました'));
    }
    const back = h('button', 'sb-rs-back', '図鑑に戻る');
    back.type = 'button';
    back.addEventListener('click', () => this.finish({ status: 'sent', result }));
    body.append(top, tag, back);
    this.page.replaceChildren(body);
    this.page.hidden = false;
  }

  // ------------------------------------------------------------ 送れなかったとき（㉛d）

  private clearOverlay(): void {
    this.root.querySelectorAll('.sb-dim, .sb-fail').forEach((n) => n.remove());
  }

  private async showFail(code: string | 'queued'): Promise<void> {
    if (this.finished) return;
    this.stage = 'fail';
    if (this.slowTimer) clearTimeout(this.slowTimer);
    this.root.dataset.stage = 'fail';
    const pending = code === 'queued' || code === 'network' ? (await outbox.pending()).length || 1 : 0;
    const scr = failScreen(code, {
      kind: this.req.kind,
      pending,
      hornet: hornetInfo(store.state, this.req.areaId, serverNow()),
    });
    this.drawFail(scr);
  }

  private drawFail(scr: FailScreen): void {
    this.clearOverlay();
    const dim = h('div', 'sb-dim');
    const slip = h('div', 'sb-fail');
    slip.setAttribute('role', 'alertdialog');
    const hd = h('h3', undefined, scr.heading);
    slip.setAttribute('aria-label', scr.heading);
    if (scr.status) slip.append(h('p', 'sb-fail-st', scr.status));
    slip.append(hd);
    if (scr.text) slip.append(h('p', 'sb-fail-tx', scr.text));
    const btns = h('div', 'sb-fail-btn');
    for (const b of scr.buttons) {
      const btn = h('button', b.primary ? 'sb-p' : 'sb-s', b.label);
      btn.type = 'button';
      btn.addEventListener('click', () => void this.onFailAction(b.action, scr, btn));
      btns.append(btn);
    }
    slip.append(btns);
    this.root.append(dim, slip);
    (btns.firstElementChild as HTMLElement | null)?.focus();
  }

  private async onFailAction(action: FailAction, scr: FailScreen, btn: HTMLButtonElement): Promise<void> {
    if (this.stage === 'flushing') return;
    switch (action) {
      case 'back':
        if (scr.variant === 'net') this.finish({ status: 'queued' });
        else if (this.lastError) this.finish({ status: 'rejected', error: this.lastError });
        else this.finish({ status: 'rejected', error: new RpcError('unknown') });
        return;
      case 'flush': {
        // 流れの外では送らない。同じ提出を outbox が1本で送る（settled で受け取る）
        this.stage = 'flushing';
        btn.disabled = true;
        btn.textContent = '送っています';
        await outbox.flush();
        if (this.stage === 'flushing' && !this.finished) await this.showFail('queued');
        return;
      }
      case 'resend':
        this.clearOverlay();
        this.setSendingUi();
        await this.send();
        return;
      case 'reshoot':
        this.clearOverlay();
        this.openCamera();
        return;
    }
  }

  // ------------------------------------------------------------ 終わり

  private finish(o: PhotoSubmitOutcome): void {
    if (this.finished) return;
    this.finished = true;
    this.stage = 'done';
    if (this.slowTimer) clearTimeout(this.slowTimer);
    this.unsub?.();
    if (this.item) releaseItem(this.item.id);
    if (this.photo) URL.revokeObjectURL(this.photo.url);
    this.root.remove();
    flowEnded();
    this.resolveFn(o);
  }
}

/**
 * 写真の提出の流れ。図鑑・地図・お題が同じものを使う（main.ts が ctx.submitPhoto に渡す）。
 * 呼ぶのは必ずボタンを押した操作の中で（カメラの起動は同期的に行う）。
 * host の dataset.sbLayout が 'book' なら図鑑の頁の位置に、それ以外は host いっぱいの紙に出す。
 */
export function createPhotoSubmit(fx: Fx): (host: HTMLElement, req: PhotoSubmitRequest) => Promise<PhotoSubmitOutcome> {
  return (host, req) => new Flow(fx, host, req).run();
}

export { isOwnedByFlow, whenFlowIdle } from './owned';
export { settledNoticeBody, SETTLED_NOTICE_HEADING } from './errors';
